import { atom } from 'jotai';
import type { Powertrain } from './powertrainAtom';
import { userSubAtom } from './authAtoms';

// 直近選択した計算セット。ヘッダーから再選択するために必要な情報のみ保持する。
// R/L で Config の形が異なる（R は unit_id を持つ）ため unit_id は任意とする構造的 superset。
export type RecentCalcTool = 'r' | 'l';

export interface RecentCalcConfig {
    project_id: string;
    destination_id: string;
    lot_id: string;
    phase_id: string;
    unit_id?: string;
    calculation_id: string;
    condition_id?: string;
    graph_id?: string;
}

export interface RecentCalc {
    tool: RecentCalcTool;
    powertrain: Powertrain;
    config: RecentCalcConfig;
    label: string;
}

export const MAX_RECENT_CALCS = 10;

// 計算セットのパスを表示用ラベルへ（id をそのまま ' / ' で連結。getMappingLabel 非依存の raw id）。
export const buildCalcLabel = (config: RecentCalcConfig): string =>
    [
        config.project_id,
        config.destination_id,
        config.lot_id,
        config.phase_id,
        config.unit_id,
        config.calculation_id,
    ]
        .filter((id): id is string => Boolean(id))
        .join(' / ');

// 同一計算セット判定キー（tool + powertrain + 階層 id）。重複排除に用いる。
const identityKey = (entry: RecentCalc): string =>
    [
        entry.tool,
        entry.powertrain,
        entry.config.project_id,
        entry.config.destination_id,
        entry.config.lot_id,
        entry.config.phase_id,
        entry.config.unit_id ?? '',
        entry.config.calculation_id,
    ].join('|');

// 先頭最新・重複排除・最大 MAX_RECENT_CALCS 件。純粋関数（単体テスト対象）。
export const addRecentCalc = (list: RecentCalc[], entry: RecentCalc): RecentCalc[] => {
    const key = identityKey(entry);
    return [entry, ...list.filter((e) => identityKey(e) !== key)].slice(0, MAX_RECENT_CALCS);
};

// 履歴は Cognito ユーザー（sub）単位で分離する。静的キーの atomWithStorage では分離できないため、
// sub 由来のキー recentCalcs:<sub> を直接読み書きする。sub 未取得時は guest キーへフォールバック。
const storageKey = (sub: string | null): string => `recentCalcs:${sub ?? 'guest'}`;

const loadRecent = (sub: string | null): RecentCalc[] => {
    try {
        const raw = localStorage.getItem(storageKey(sub));
        if (!raw) return [];
        const parsed: unknown = JSON.parse(raw);
        return Array.isArray(parsed) ? (parsed as RecentCalc[]) : [];
    } catch {
        return [];
    }
};

const saveRecent = (sub: string | null, list: RecentCalc[]): void => {
    try {
        localStorage.setItem(storageKey(sub), JSON.stringify(list));
    } catch {
        // localStorage 不可（quota / プライベートモード等）は致命的でないため握りつぶす。
    }
};

// sub が変わると別キーを読むためのメモリキャッシュ（sub 単位で in-memory にも分離）。
const cacheAtom = atom<{ sub: string | null; list: RecentCalc[] } | null>(null);

// 現在の sub に対応する履歴。キャッシュが現 sub と一致すればそれを、なければ localStorage から読む。
export const recentCalcsAtom = atom<RecentCalc[]>((get) => {
    const sub = get(userSubAtom);
    const cache = get(cacheAtom);
    if (cache && cache.sub === sub) return cache.list;
    return loadRecent(sub);
});

// 履歴へ記録する write-only atom。dedup + 先頭最新 + 最大件数を適用し、localStorage とキャッシュへ反映。
export const recordRecentCalcAtom = atom(null, (get, set, entry: RecentCalc) => {
    const sub = get(userSubAtom);
    const cache = get(cacheAtom);
    const current = cache && cache.sub === sub ? cache.list : loadRecent(sub);
    const next = addRecentCalc(current, entry);
    saveRecent(sub, next);
    set(cacheAtom, { sub, list: next });
});
